import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import PropTypes from "prop-types";

import api from "api/api";

import CircularProgress from "@mui/material/CircularProgress";

//import components
import ConsumerFilter from "../ConsumerFilter";
import ProductInfo from "../ProductInfo";

import ColumnVisibility from "components/common/ColumnVisibility";
import VariableVisibility from "components/common/analysisModule/chart/VariableVisibility";

// redux
import { useSelector, useDispatch } from "react-redux";

import HeaderSampleSize from "./HeaderSampleSize";
import SkewTabs from "./SkewTabs";
import AnalysisTableBottomLabel from "./AnalysisTableBottomLabel";

import { getScoreByVariable, getProductColumnClassName, getSelectedOption, getFormattedScoreOrNA, getDataView, checkOverallDataPresent, getSortedDatabyOverallMeanScore, getSortedDataByUlAndCompetitor, sortMultipleStudies } from "../../../utils/analysisTable/analysisUtils";

import { getTestResultByVariable, getColorsAndLetterForTransposedTable } from "../../../utils/analysisTable/tTestutils";
import { showNotification } from "store/features/notificationSlice";

import TableCellWithInnerSeparator from "./TableCellWithInnerSeparator";
import TableCellWithOuterSeparator from "./TableCellWithOuterSeparator";
import { loadProductOrderState } from "store/features/productsInfoSlice";

// mui components
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";

// mui icons
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const LightTooltip = styled(({ className, ...props }) => (
    <Tooltip
        {...props}
        classes={{ popper: className }}
    />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: "rgba(216, 238, 255, 0.6)",
        color: "rgba(0, 0, 0, 0.87)",
        boxShadow: theme.shadows[1],
        fontSize: "0.55rem",
        width: "130px",
    },
}));

function AnalysisTableTransposed({ productsSecondaryData = [], handleFilterSubmit = () => {}, analysisLevelData = {}, tTest = {}, settings = {}, isTableShown = true, pageContext = "" }) {
    const dispatch = useDispatch();
    const { isITMDataShown = false, tableConfig, moduleName, bottomLabelModuleName, boxAnalysisTypeSelection = [], boxAnalysisSelection = {}, analysisTypeValue, mappings, cohensDResult = [] } = analysisLevelData;
    const { tTestLegends, isFisher, tTestResult, testLabel } = tTest;
    const { activeTab, setActiveTab } = settings;
    const { stickyColumns = [] } = tableConfig;
    const isConsumerFilterLoadingStoreData = useSelector((state) => state.consumerFilterStore.isLoading);
    const weightToggle = useSelector((state) => state.analysisResultsStore.weightToggle);
    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);
    const removedKpiStoreState = useSelector((state) => state.analysisKpiStore[`${moduleName}RemovedKpiList`]);

    const overallToggle = useSelector((state) => state.analysisResultsStore.overallToggle);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);

    const productColumnVisibility = useSelector((state) => state.productsInfoStore.productsVisibility);
    // tTest
    const testAnalysisType = useSelector((state) => state.tTestStore.testAnalysisType);
    const testJarAnalysisType = useSelector((state) => state.tTestStore.testJarAnalysisType);

    // dataview
    const overallAnalysisTypeValue = useSelector((state) => state.analysisTypeSelectionStore.overallAnalysisTypeValue);

    const [productDetailsDialogueOpen, setProductDetailsDialogueOpen] = useState(false);
    const likertScaleData = useSelector((state) => state.analysisResultsStore.likertScaleData);
    const selectedStudies = useSelector((state) => state.studyDataStore.selectedStudies);
    const isUnileverFragranceHouse = process.env?.REACT_APP_UNILEVER_FRAGRANCE_HOUSE?.toLowerCase() === productCategoryData?.label?.toLowerCase();

    const overallVariable = isUnileverFragranceHouse ? "R_OOF" : "R_OO_Frag_10pt";

    // column drag and drop variables
    const [draggableColumns, setDraggableColumns] = useState([]);

    const tableRef = useRef(null);
    const tbodyRef = useRef(null);

    const [actionStandardKpis, setActionStandardKpis] = useState([]);
    const [actionStandardData, setActionStandardData] = useState([]);

    const [removedKpi, setRemovedKpi] = useState([]);

    const isOralCare = process.env?.REACT_APP_ORAL_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();

    const [box, setBox] = useState(false);

    const [dataViewKey, setDataViewKey] = useState("");

    const [selectedProductInfo, setSelectedProductInfo] = useState({});
    const significanceToggle = useSelector((state) => state.analysisResultsStore.significanceToggle);
    

    useEffect(() => {
        if (productsSecondaryData.length) {
            let sortedData;
            if (selectedStudies.length === 1) {
                const isOverallExists = checkOverallDataPresent(productsSecondaryData, overallVariable);
                sortedData = isOverallExists ? getSortedDatabyOverallMeanScore(productsSecondaryData, overallVariable) : getSortedDataByUlAndCompetitor(productsSecondaryData);
                setDraggableColumns(sortedData);
                const updatedColumns = [...sortedData];
                const _updateArray = updatedColumns.map((ele) => {
                    return {
                        productCode: ele.title,
                        coreDataId: ele.coreDataId,
                        isCompetitor: ele.isCompetitorProduct,
                    };
                });
                dispatch(loadProductOrderState([..._updateArray]));
            } else {
                sortedData = sortMultipleStudies(productsSecondaryData, overallVariable);
                setDraggableColumns(sortedData);
                const updatedColumns = [...sortedData];
                const _updateArray = updatedColumns.map((ele) => {
                    return {
                        productCode: ele.title,
                        coreDataId: ele.coreDataId,
                        isCompetitor: ele.isCompetitorProduct,
                    };
                });
                dispatch(loadProductOrderState([..._updateArray]));
            }
        }
    }, [productsSecondaryData]);

    useEffect(() => {
        setRemovedKpi([...removedKpiStoreState]);
    }, [moduleName, removedKpiStoreState]);

    useEffect(() => {
        const _dataViewKey = getDataView(analysisTypeValue, weightToggle, boxAnalysisTypeSelection);
        const { value } = analysisTypeValue;
        setBox(value === "box_analysis");
        setDataViewKey(_dataViewKey);
    }, [analysisTypeValue, boxAnalysisTypeSelection, weightToggle]);

    useEffect(() => {
        const { value } = analysisTypeValue;
        setBox(value === "box_analysis");
    }, [analysisTypeValue, boxAnalysisTypeSelection]);

    const addBorders = useCallback(() => {
        if (tbodyRef.current) {
            const rows = Array.from(tbodyRef.current.querySelectorAll("tr.score-row"));
            let currentGroupLastRowIndex = 0;
            rows.forEach((row, index) => {
                const tds = row.querySelectorAll("td");
                const firstTd = row.querySelector("td");
                tds.forEach((td, i) => {
                    // clear all border styles
                    td.style.borderTop = "";
                    td.style.borderBottom = "";
                    td.style.borderRight = "";
                    td.style.borderLeft = "";
                    td.style.borderTopLeftRadius = "";
                    td.style.borderTopRightRadius = "";
                    td.style.borderBottomLeftRadius = "";
                    td.style.borderBottomRightRadius = "";
                    td.style.borderBottomWidth = "0px";

                    if (firstTd.hasAttribute("rowspan")) {
                        currentGroupLastRowIndex = index + firstTd.rowSpan - 1;
                        firstTd.style.borderTopLeftRadius = "8px"; // Round the top left corner
                        firstTd.style.borderBottomLeftRadius = "8px"; // Round the bottom left corner
                        firstTd.style.borderLeft = "2px solid #005eef80"; // Add border on the left
                        firstTd.style.borderBottom = "2px solid #005eef80";
                        td.style.borderTop = "2px solid #005eef80";
                        if (i === tds.length - 1) {
                            td.style.borderTopRightRadius = "8px"; // Round the top left corner
                            td.style.borderRight = "2px solid #005eef80";
                        }
                    } else if (i === tds.length - 1) {
                        td.style.borderRight = "2px solid #005eef80";
                    }

                    // identifying the last row and put bottom border
                    if (currentGroupLastRowIndex === index && i < tds.length - 1) {
                        td.style.borderBottom = "2px solid #005eef80";
                    } else if (currentGroupLastRowIndex === index && i === tds.length - 1) {
                        td.style.borderBottomRightRadius = "8px";
                        td.style.borderBottom = "2px solid #005eef80";
                    }
                });
            });
        }
    }, []);

    // useEffect hook to call addBorders after a delay upon certain dependencies changing
    useEffect(() => {
        if (moduleName === "overall") {
            const addBr = setTimeout(() => {
                addBorders(); // Call the addBorders function after the delay
            }, 10); // Wait for 10 milliseconds

            // Cleanup function to clear the timeout if the component unmounts or dependencies change
            return () => clearTimeout(addBr);
        }
    }, [addBorders, mappings, draggableColumns, productColumnVisibility, overallToggle, removedKpi, overallAnalysisTypeValue, moduleName, isITMDataShown]); // Dependencies that trigger the effect

    const getKpisFromResponse = (response) => {
        const kpis = new Set(
            response
                .map((datum) => {
                    const _kpi = datum?.action_standard_kpis?.map((item) => item?.kpi || []);
                    return _kpi.flat();
                })
                .flat()
        );

        return [...kpis];
    };
    const getActionStandard = useCallback(() => {
        if (selectedStudy.length === 1) {
            const coreDataId = selectedStudy[0]?.id || null;
            if (coreDataId) {
                const url = process.env.REACT_APP_SERVER_PATH + `/action-standard-groups/submit/kpis/?archive=False&core_data_model=${coreDataId}`;
                try {
                    api.get(url)
                        .then(async (res) => {
                            const response = res.data;
                            const kpis = getKpisFromResponse(response);
                            const kpisArray = kpis.map((item) => item.backend_variable);
                            setActionStandardKpis([...kpisArray]);
                            setActionStandardData(response);
                        })
                        .catch(async (error) => {
                            handleError("error", error);
                        });
                } catch (error) {
                    handleError("error", error);
                }
            }
        }
    }, [selectedStudy, moduleName, isOralCare]);

    useEffect(() => {
        getActionStandard();
    }, [getActionStandard]);

    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };

    const hasValidMean = (score) => score.mean !== null && score.mean !== "";

    const productHasBackendVariable = (backendVariable) => (product) => product.scores?.some((score) => score.backendVariable === backendVariable && hasValidMean(score));

    const isSubCategoryVisible = (sub) => draggableColumns.some(productHasBackendVariable(sub.backendVariable));

    const isSubCategoryVisibleAndNotRemoved = (sub) => !removedKpiStoreState.includes(sub.backendVariable) && isSubCategoryVisible(sub);

    const getProcessedAndSortedMacros = useCallback(
        (filterFn) => {
            let processedMacros = mappings
                .map((macro) => {
                    const subCategories = macro.displayLabels.filter(filterFn);
                    return { ...macro, subCategories };
                })
                .filter((macro) => macro.subCategories.length > 0);

            // Sort sub-categories within each macro-category
            processedMacros.forEach((macro) => {
                macro.subCategories.sort((a, b) => {
                    if (macro.macroCategory === "Overall Fragrance") {
                        if (a.backendVariable === overallVariable) return -1;
                        if (b.backendVariable === overallVariable) return 1;
                    }
                    return a.displayLabel.localeCompare(b.displayLabel);
                });
            });

            // Sort the macro-categories themselves
            processedMacros.sort((a, b) => {
                if (a.macroCategory === "Overall Fragrance") return -1;
                if (b.macroCategory === "Overall Fragrance") return 1;
                if (a.macroCategory.toLowerCase().includes("overall") && !b.macroCategory.toLowerCase().includes("overall")) return -1;
                if (!a.macroCategory.toLowerCase().includes("overall") && b.macroCategory.toLowerCase().includes("overall")) return 1;
                return a.macroCategory.localeCompare(b.macroCategory);
            });

            return processedMacros;
        },
        [mappings]
    );

    const variableVisibilityMappings = useMemo(() => {
        const allSortedMacros = getProcessedAndSortedMacros(isSubCategoryVisible);
        return allSortedMacros.flatMap((macro) => macro.subCategories);
    }, [getProcessedAndSortedMacros, draggableColumns]);

    const headerConfig = useMemo(() => {
        return getProcessedAndSortedMacros(isSubCategoryVisibleAndNotRemoved);
    }, [getProcessedAndSortedMacros, draggableColumns, removedKpiStoreState]);

    const renderSubHeadingCohensDCell = (cohensDItem, className, colSpan) => {
        const formattedValue = `Effect vs ${cohensDItem.product_code} / ${cohensDItem.study_reference_title}`;
        return (
            <TableCellWithOuterSeparator
                key={formattedValue}
                value={formattedValue}
                cellType="td"
                testLabel=""
                mainProps={{
                    colSpan: colSpan,
                    align: "center",
                    className: className,
                }}
                styles={{
                    main: {
                        fontWeight: "600",
                        cursor: "move",
                        minWidth: 200,
                        maxWidth: 300,
                    },
                    wrapper: {
                        justifyContent: "space-between",
                    },
                    value: {
                        alignItems: "center",
                        justifyContent: "center",
                    },
                }}
            />
        );
    };

    const renderSubHeadingMainCell = (sub, className, colSpan, likertScale) => {
        let headerText = `${sub.displayLabel}`;
        if (likertScale) {
            headerText += ` (${likertScale})`;
        }
        const showActionStandard = actionStandardKpis.includes(sub.backendVariable) && selectedStudy.length === 1;
        if (showActionStandard) {
            headerText += "*";
        }
        return (
            <TableCellWithOuterSeparator
                key={sub.backendVariable}
                value={`${headerText}`}
                cellType="td"
                testLabel=""
                mainProps={{
                    colSpan: colSpan,
                    align: "center",
                    className: className,
                }}
                styles={{
                    main: {
                        fontWeight: showActionStandard ? 600 : 500,
                        cursor: "move",
                        minWidth: 200,
                        maxWidth: 300,
                    },
                    wrapper: {
                        justifyContent: "space-between",
                    },
                    value: {
                        alignItems: "center",
                        justifyContent: "center",
                    },
                }}
            />
        );
    };

    const renderSubHeadingCohensDCells = (cohensDList, className, colSpan) => {
        return cohensDList.map((cohensDItem) => {
            return renderSubHeadingCohensDCell(cohensDItem, className, colSpan);
        });
    };

    const getSubHeadingCells = (macro, index) => {
        const tds = macro.subCategories.map((sub) => {
            const className = `macroCategory_${index}`;
            const colSpan = macro.macroCategory.toLowerCase().includes("jar") && box ? "3" : dataViewKey.split(",").length;
            const likertScale = likertScaleData[sub?.backendVariable];
            const cohensDList = cohensDResult.length > 0 && !box ? cohensDResult.filter((res) => res.backend_variable === sub.backendVariable) : [];

            return (
                <>
                    {renderSubHeadingMainCell(sub, className, colSpan, likertScale)}
                    {renderSubHeadingCohensDCells(cohensDList, className, colSpan)}
                </>
            );
        });

        return tds;
    };

    const renderStickyHeaderCell = (col, i) => {
        const mainStyles = col === "Consumer Filters" ? { minWidth: 200, maxWidth: 200, left: i * 200 + 100 } : { minWidth: 200, maxWidth: 200, position: "sticky", left: i * 200 + 100, zIndex: 2 };
        if (i === 0) {
            return (
                <TableCellWithInnerSeparator
                    key={col + i}
                    value={col}
                    cellType="th"
                    testLabel=""
                    styles={{
                        main: {
                            minWidth: 300,
                            maxWidth: 300,
                            position: "sticky",
                            left: 0,
                            zIndex: 2,
                        },
                        wrapper: {},
                        value: {
                            alignItems: "center",
                        },
                    }}
                    mainProps={{
                        colSpan: 1,
                        rowSpan: 2,
                        align: "center",
                    }}
                />
            );
        }

        return (
            <TableCellWithOuterSeparator
                key={col + i}
                value={col}
                cellType="th"
                testLabel=""
                styles={{
                    main: mainStyles,
                    wrapper: {
                        justifyContent: "space-between",
                    },
                    value: {
                        alignItems: "center",
                        justifyContent: "center",
                    },
                }}
                mainProps={{
                    colSpan: 1,
                    rowSpan: 2,
                    textAlign: "center",
                    justifyContent: "center",
                }}
            />
        );
    };

    const renderHeaderMacroCell = (macro, index) => {
        const className = `macroCategory_${index}`;
        let colSpan = macro.macroCategory.toLowerCase().includes("jar") && box ? "3" : macro.subCategories.length * dataViewKey.split(",").length;
        if (cohensDResult.length > 0 && !box) {
            const res = macro.subCategories.map((sub) => sub.backendVariable);
            const len = cohensDResult.filter((resItem) => res.includes(resItem.backend_variable)).length;
            colSpan += len;
        }

        return (
            <TableCellWithOuterSeparator
                key={macro.macroCategory}
                value={macro?.macroCategory}
                cellType="th"
                testLabel=""
                mainProps={{
                    colSpan: colSpan,
                    align: "center",
                    className: className,
                }}
                styles={{
                    main: {
                        fontWeight: "600",
                        cursor: "move",
                        minWidth: 200,
                        maxWidth: 300,
                        background: "",
                        borderColor: "",
                    },
                    wrapper: {
                        justifyContent: "space-between",
                    },
                    value: {
                        alignItems: "center",
                        justifyContent: "center",
                    },
                }}
            />
        );
    };

    const returnTableHeader = () => {
        return (
            <>
                <tr>
                    {stickyColumns.map((col, i) => renderStickyHeaderCell(col, i))}
                    {headerConfig.map((macro, index) => renderHeaderMacroCell(macro, index))}
                </tr>
                <tr>
                    {headerConfig.flatMap((macro, index) => {
                        return getSubHeadingCells(macro, index);
                    })}
                </tr>
            </>
        );
    };

    const returnConsumerFilter = (datum) => {
        return !isConsumerFilterLoadingStoreData ? (
            <ConsumerFilter
                productsSecondaryData={draggableColumns}
                product={datum}
                handleFilterSubmit={handleFilterSubmit}
            />
        ) : (
            <CircularProgress size={20} />
        );
    };

    const renderBoxAnalysisBottomCell = () => {
        return (
            <TableCellWithOuterSeparator
                key={`box-analysis-bottom-0`}
                value={
                    <>
                        Less Intense
                        <br />
                        than I like
                        <br />
                        <br />
                        (Bottom 2 Box)
                    </>
                }
                cellType="td"
                testLabel=""
                mainProps={{
                    rowSpan: 2,
                }}
                styles={{
                    main: {
                        fontWeight: "600",
                        cursor: "pointer",
                        background: "",
                    },
                    wrapper: {},
                    value: {
                        justifyContent: "center",
                        color: "#005eef",
                        background: "#f0f8ff",
                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                        fontWeight: "600",
                        borderRadius: "5px",
                        padding: "5px",
                    },
                }}
                showSeperator={true}
            />
        );
    };

    const renderBoxAnalysisMiddleCell = () => {
        return (
            <TableCellWithOuterSeparator
                key={`box-analysis-middle-0`}
                value={
                    <>
                        Just How I like
                        <br />
                        <br />
                        (Middle Box)
                    </>
                }
                cellType="td"
                testLabel=""
                mainProps={{
                    rowSpan: 2,
                }}
                styles={{
                    main: {
                        fontWeight: "600",
                        cursor: "pointer",
                        background: "",
                    },
                    wrapper: {},
                    value: {
                        justifyContent: "center",
                        color: "#005eef",
                        background: "#f0f8ff",
                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                        fontWeight: "600",
                        borderRadius: "5px",
                        padding: "5px",
                    },
                }}
                showSeperator={false}
            />
        );
    };

    const renderBoxAnalysisTopCell = () => {
        return (
            <TableCellWithOuterSeparator
                key={`box-analysis-top-0`}
                value={
                    <>
                        More Intense
                        <br />
                        than I like
                        <br />
                        <br />
                        (Top 2 Box)
                    </>
                }
                cellType="td"
                testLabel=""
                mainProps={{
                    rowSpan: 2,
                }}
                styles={{
                    main: {
                        fontWeight: "600",
                        cursor: "pointer",
                        background: "",
                    },
                    wrapper: {},
                    value: {
                        justifyContent: "center",
                        color: "#005eef",
                        background: "#f0f8ff",
                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                        fontWeight: "600",
                        borderRadius: "5px",
                        padding: "5px",
                    },
                }}
                showSeperator={false}
            />
        );
    };

    const renderBoxHeadingJarCells = () => {
        return (
            <>
                {renderBoxAnalysisBottomCell()}
                {renderBoxAnalysisMiddleCell()}
                {renderBoxAnalysisTopCell()}
            </>
        );
    };

    const renderBoxHeadingNonJarCell = (ele, i, index, k) => {
        const key = "box-analysis-recall-kpi-label-column-" + index + "-" + k + "-" + i;
        const label = ele;
        return (
            <TableCellWithOuterSeparator
                key={key}
                value={label}
                cellType="td"
                testLabel=""
                styles={{
                    main: {
                        fontWeight: "600",
                        cursor: "pointer",
                        background: "",
                    },
                    wrapper: {},
                    value: {
                        justifyContent: "center",
                        color: "#005eef",
                        background: "#f0f8ff",
                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                        fontWeight: "600",
                        borderRadius: "5px",
                        padding: "5px",
                    },
                }}
                showSeperator={ele !== "Bottom Box"}
            />
        );
    };

    const renderBoxHeadingNonJarCells = (index, k) => {
        return (
            <>
                {boxAnalysisTypeSelection.map((ele, i) => {
                    return renderBoxHeadingNonJarCell(ele, i, index, k);
                })}
            </>
        );
    };

    const getBoxHeadingCells = (macro, index) => {
        const tds = macro.subCategories.map((sub, k) => {
            if (macro.macroCategory.toLowerCase().includes("jar")) {
                return renderBoxHeadingJarCells();
            } else {
                return renderBoxHeadingNonJarCells(index, k);
            }
        });

        return tds;
    };

    const renderBoxAnalysisSelectionCell = (ele, i, index, k, selectionFilteredData) => {
        const key = "box-analysis-recall-product-label-column-" + index + "-" + k + "-" + i;
        return (
            <TableCellWithOuterSeparator
                key={key}
                value={getSelectedOption(ele.split(" ")[0].toLowerCase(), selectionFilteredData)}
                cellType="td"
                testLabel=""
                styles={{
                    main: {
                        fontWeight: "600",
                        cursor: "pointer",
                        background: "",
                    },
                    wrapper: {},
                    value: {
                        justifyContent: "center",
                        color: "#005eef",
                        background: "#f0f8ff",
                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                        fontWeight: "600",
                        borderRadius: "5px",
                        padding: "5px",
                    },
                }}
                showSeperator={ele !== "Bottom Box"}
            />
        );
    };

    const renderBoxAnalysisSelectionCells = (index, k, selectionFilteredData) => {
        return (
            <>
                {boxAnalysisTypeSelection.map((ele, i) => {
                    return renderBoxAnalysisSelectionCell(ele, i, index, k, selectionFilteredData);
                })}
            </>
        );
    };

    const getBoxAnalysisSelectionCells = (macro, index) => {
        const tds = macro.subCategories.map((sub, k) => {
            const selectionFilteredData = boxAnalysisSelection[sub.backendVariable];
            if (!macro.macroCategory.toLowerCase().includes("jar") && box) {
                return renderBoxAnalysisSelectionCells(index, k, selectionFilteredData);
            }
        });

        return tds;
    };

    const returnBoxAnalysisLabelRow = () => {
        return (
            <>
                <tr>
                    <TableCellWithInnerSeparator
                        key={"box-analysis-column"}
                        value={"Box Type Analysis"}
                        cellType="th"
                        testLabel=""
                        styles={{
                            main: {
                                minWidth: 500,
                                maxWidth: 500,
                                position: "sticky",
                                left: 0,
                                zIndex: 2,
                            },
                            wrapper: {},
                            value: {
                                alignItems: "center",
                            },
                        }}
                        mainProps={{
                            colSpan: 2,
                            rowSpan: 2,
                            align: "center",
                        }}
                    />
                    <TableCellWithOuterSeparator
                        key={"box-analysis-column-2"}
                        value={""}
                        cellType="th"
                        testLabel=""
                        styles={{
                            main: {
                                minWidth: 200,
                                maxWidth: 200,
                                left: 0,
                                zIndex: 2,
                            },
                            wrapper: {},
                            value: {
                                alignItems: "center",
                            },
                        }}
                        mainProps={{
                            colSpan: 1,
                            rowSpan: 2,
                            align: "center",
                        }}
                    />
                    {box &&
                        headerConfig.flatMap((macro, index) => {
                            return getBoxHeadingCells(macro, index);
                        })}
                </tr>
                <tr>
                    {box &&
                        headerConfig.flatMap((macro, index) => {
                            return getBoxAnalysisSelectionCells(macro, index);
                        })}
                </tr>
            </>
        );
    };

    const renderBoxSignificanceCells = (className) => {
        const jarTTestResult = tTestResult.find((ele) => ele?.backendVariable.includes("JAR"));
        return (
            <>
                <TableCellWithOuterSeparator
                    key={`bottom-box-significance`}
                    value={jarTTestResult?.[`bottom_significance`]}
                    cellType="th"
                    testLabel=""
                    mainProps={{
                        className: className,
                    }}
                    styles={{
                        main: {
                            backgroundColor: "#fff",
                            border: "",
                            outline: "",
                        },
                        wrapper: {},
                        value: {
                            justifyContent: "center",
                            background: "#f0f8ff",
                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                            borderRadius: "5px",
                            padding: "5px",
                        },
                    }}
                    showSeperator={true}
                />
                <TableCellWithOuterSeparator
                    key={`middle-box-significance`}
                    value={jarTTestResult?.[`middle_significance`]}
                    cellType="th"
                    testLabel=""
                    mainProps={{
                        className: className,
                    }}
                    styles={{
                        main: {
                            backgroundColor: "#fff",
                            border: "",
                            outline: "",
                        },
                        wrapper: {},
                        value: {
                            justifyContent: "center",
                            background: "#f0f8ff",
                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                            borderRadius: "5px",
                            padding: "5px",
                        },
                    }}
                    showSeperator={false}
                />
                <TableCellWithOuterSeparator
                    key={`top-box-significance`}
                    value={jarTTestResult?.[`top_significance`]}
                    cellType="th"
                    testLabel=""
                    mainProps={{
                        className: className,
                    }}
                    styles={{
                        main: {
                            backgroundColor: "#fff",
                            border: "",
                            outline: "",
                        },
                        wrapper: {},
                        value: {
                            justifyContent: "center",
                            background: "#f0f8ff",
                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                            borderRadius: "5px",
                            padding: "5px",
                        },
                    }}
                    showSeperator={false}
                />
            </>
        );
    };

    const renderSignificanceCell = (ele, i, index, macro, className, testResultForColumn) => {
        const result = testResultForColumn || {};
        const significanceKeyArr = ele.split("_");
        const significanceValue = (significanceKeyArr[0] === "mean" ? result.significance_pvalue : result[`${significanceKeyArr[0]}_significance`]) ?? "NA";
        const showSignificance = !macro.macroCategory.toLowerCase().includes("jar") ? significanceValue : "";
        return (
            <TableCellWithOuterSeparator
                key={`${macro.macroCategory}-${i}`}
                value={showSignificance}
                cellType="th"
                testLabel=""
                mainProps={{
                    colSpan: 1,
                    // draggable: true,
                    // onDragStart: (e) => handleColumnDragStart(e, index),
                    // onDragOver: handleDragOver,
                    // onDrop: () => handleColumnDrop(index),
                    align: "center",
                    className: className,
                }}
                styles={{
                    main: {
                        fontWeight: "600",
                        cursor: "move",
                        minWidth: 200,
                        maxWidth: 300,
                        background: "",
                        borderColor: "",
                    },
                    wrapper: {
                        justifyContent: "space-between",
                    },
                    value: {
                        alignItems: "center",
                        justifyContent: "center",
                    },
                }}
            />
        );
    };

    const renderNonBoxSignificanceCells = (index, macro, className, testResultForColumn) => {
        return (
            <>
                {dataViewKey.split(",").map((ele, i) => {
                    return renderSignificanceCell(ele, i, index, macro, className, testResultForColumn);
                })}
            </>
        );
    };

    const getSignificanceCells = (macro, index) => {
        const tds = macro.subCategories.map((sub, k) => {
            const className = `pvalue_${index}_${k}`;
            const testResultForColumn = tTestResult.find((res) => res.backendVariable === sub.backendVariable);
            if (macro.macroCategory.toLowerCase().includes("jar") && box) {
                return renderBoxSignificanceCells(className);
            } else {
                return renderNonBoxSignificanceCells(index, macro, className, testResultForColumn);
            }
        });

        return tds;
    };

    const returnSignificanceRow = () => {
        return (
            <tr>
                <TableCellWithInnerSeparator
                    value={"Significance (p value)"}
                    cellType="th"
                    testLabel=""
                    styles={{
                        main: {
                            minWidth: 300,
                            maxWidth: 300,
                            position: "sticky",
                            left: 0,
                            zIndex: 2,
                        },
                        wrapper: {},
                        value: {
                            alignItems: "center",
                        },
                    }}
                    mainProps={{
                        colSpan: 2,
                        align: "center",
                    }}
                />

                <TableCellWithOuterSeparator
                    value={
                        <i>
                            p-value comparison between {tTestResult[0]?.colors[0]?.productCode} <br />
                            and {tTestResult[0]?.colors[1]?.productCode}
                        </i>
                    }
                    cellType="td"
                    testLabel=""
                    styles={{
                        main: {
                            minWidth: 200,
                            maxWidth: 200,
                            left: 300,
                        },
                        wrapper: {
                            justifyContent: "space-between",
                        },
                        value: {
                            alignItems: "center",
                            justifyContent: "center",
                        },
                    }}
                    mainProps={{
                        colSpan: 1,
                        textAlign: "center",
                        justifyContent: "center",
                    }}
                />

                {headerConfig.flatMap((macro, index) => {
                    return getSignificanceCells(macro, index);
                })}
            </tr>
        );
    };

    const renderBoxScoreCells = (index, _score, _testResult, className) => {
        const key = [`bottom-2-box-${index}`, `middle-box-${index}`, `top-2-box-${index}`];
        return (
            <>
                <TableCellWithOuterSeparator
                    key={key[0]}
                    value={getFormattedScoreOrNA(_score["bottom_per"], "bottom")}
                    cellType="td"
                    testLabel={_testResult?.["label"]?.["bottom_per"] ?? null}
                    mainProps={{
                        className: className,
                    }}
                    styles={{
                        main: {
                            backgroundColor: "#fff",
                            border: "",
                            outline: "",
                        },
                        wrapper: {},
                        value: {
                            justifyContent: "center",
                            background: _testResult?.["color"]?.["bottom_per"] ?? "#fff",
                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                            borderRadius: "5px",
                            padding: "5px",
                        },
                    }}
                    showSeperator={true}
                />
                <TableCellWithOuterSeparator
                    key={key[1]}
                    value={getFormattedScoreOrNA(_score["middle_per"], "middle")}
                    cellType="td"
                    testLabel={_testResult?.["label"]?.["middle_per"] ?? null}
                    mainProps={{
                        className: className,
                    }}
                    styles={{
                        main: {
                            backgroundColor: "#fff",
                            border: "",
                            outline: "",
                        },
                        wrapper: {},
                        value: {
                            justifyContent: "center",
                            background: _testResult?.["color"]?.["middle_per"] ?? "#fff",
                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                            borderRadius: "5px",
                            padding: "5px",
                        },
                    }}
                    showSeperator={false}
                />
                <TableCellWithOuterSeparator
                    key={key[2]}
                    value={getFormattedScoreOrNA(_score["top_per"], "top")}
                    cellType="td"
                    testLabel={_testResult?.["label"]?.["top_per"] ?? null}
                    mainProps={{
                        className: className,
                    }}
                    styles={{
                        main: {
                            backgroundColor: "#fff",
                            border: "",
                            outline: "",
                        },
                        wrapper: {},
                        value: {
                            justifyContent: "center",
                            background: _testResult?.["color"]?.["top_per"] ?? "#fff",
                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                            borderRadius: "5px",
                            padding: "5px",
                        },
                    }}
                    showSeperator={false}
                />
            </>
        );
    };

    const renderDataViewScoreCell = (ele, k, index, _score, _testResult, macro, className) => {
        const key = "box-analysis-recall-value-column-" + index + "-" + k;
        return (
            <TableCellWithOuterSeparator
                key={key}
                value={getFormattedScoreOrNA(_score[dataViewKey.split(",")[k]], ele.split(",")[0])}
                cellType="td"
                testLabel={!macro.macroCategory.toLowerCase().includes("jar") ? _testResult?.["label"]?.[ele] : ""}
                mainProps={{
                    className: className,
                }}
                styles={{
                    main: {
                        backgroundColor: "#fff",
                        border: "",
                        outline: "",
                    },
                    wrapper: {},
                    value: {
                        justifyContent: "center",
                        background: !macro.macroCategory.toLowerCase().includes("jar") ? _testResult?.["color"]?.[ele] : "#fff",
                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                        borderRadius: "5px",
                        padding: "5px",
                    },
                }}
                showSeperator={!ele.startsWith("bottom")}
            />
        );
    };

    const renderCohensDCell = (ele, productCodeTitle, className) => {
        const isMatch = ele.scores.find((score) => score.product_code === productCodeTitle);
        if (isMatch) {
            const color = isMatch.color ? isMatch.color : "";
            return (
                <TableCellWithOuterSeparator
                    key={`cohens-d-${ele.backend_variable}`}
                    value={
                        <div
                            style={{
                                width: "20px",
                                height: "20px",
                                backgroundColor: color,
                                border: `1px solid ${color}`,
                                borderRadius: "50%",
                            }}
                        ></div>
                    }
                    cellType="td"
                    mainProps={{
                        className: className,
                    }}
                    styles={{
                        main: {
                            backgroundColor: "#fff",
                        },
                        wrapper: {},
                        value: {
                            justifyContent: "center",
                            background: "#fff",
                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                            borderRadius: "5px",
                            padding: "5px",
                        },
                    }}
                />
            );
        }
        return (
            <TableCellWithOuterSeparator
                key={`cohens-d-warning-${ele.backend_variable}`}
                value={
                    <LightTooltip
                        title="Cohen's D is applied on products belonging to the same study reference"
                        placement="right-end"
                    >
                        <WarningAmberIcon
                            style={{
                                fontWeight: "600",
                                color: "#FFC000",
                                fontSize: "20px",
                            }}
                        />
                    </LightTooltip>
                }
                cellType="td"
                mainProps={{
                    className: className,
                }}
                styles={{
                    main: {
                        backgroundColor: "#fff",
                    },
                    wrapper: {},
                    value: {
                        justifyContent: "center",
                        background: "#fff",
                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                        borderRadius: "5px",
                        padding: "5px",
                    },
                }}
            />
        );
    };

    const renderNonBoxScoreCells = (index, macro, _score, _testResult, cohensDList, className, productCodeTitle) => {
        return (
            <>
                {dataViewKey.split(",").map((ele, k) => {
                    return renderDataViewScoreCell(ele, k, index, _score, _testResult, macro, className);
                })}
                {cohensDList.map((ele) => {
                    return renderCohensDCell(ele, productCodeTitle, className);
                })}
            </>
        );
    };

    const getJarAnalysisType = () => {
        let keys = "";
        if (testJarAnalysisType.toLowerCase() === "all") {
            keys = "top_per,bottom_per,middle_per";
        } else if (testJarAnalysisType.toLowerCase() === "top 2 box") {
            keys = "top_per";
        } else if (testJarAnalysisType.toLowerCase() === "bottom 2 box") {
            keys = "bottom_per";
        } else {
            keys = "middle_per";
        }

        return keys;
    }

    const getScoreCells = (macro, index, datum, productCodeTitle, productCodeCoreDataId) => {
        const tds = macro.subCategories.map((sub) => {
            const className = `macroCategory_${index}`;
            const _score = getScoreByVariable(datum.scores, sub?.backendVariable);
            const _test = getTestResultByVariable(tTestResult, sub?.backendVariable, productCodeCoreDataId);
            const _testAnalysisType = sub?.backendVariable?.toLowerCase().includes("jar") ? getJarAnalysisType() : testAnalysisType;
            const _testResult = getColorsAndLetterForTransposedTable(_testAnalysisType, productCodeTitle, _test);

            if (macro.macroCategory.toLowerCase().includes("jar") && box) {
                return renderBoxScoreCells(index, _score, _testResult, className);
            } else {
                const cohensDList = cohensDResult.length > 0 ? cohensDResult.filter((res) => res.backend_variable === sub.backendVariable) : [];
                return renderNonBoxScoreCells(index, macro, _score, _testResult, cohensDList, className, productCodeTitle);
            }
        });

        return tds;
    };

    const renderScoreStickyFirstCell = (col, i, datum, productCodeTitle, studyReferenceOrTestCode) => {
        return (
            <TableCellWithInnerSeparator
                key={col + i}
                value={
                    <button
                        className="clean-button color-blue"
                        style={{
                            fontWeight: 600,
                            fontSize: "0.875rem",
                        }}
                        onClick={(e) =>
                            handleProductClick(e, {
                                coreDataId: datum.coreDataId,
                                productCode: datum.title,
                            })
                        }
                    >
                        {productCodeTitle} / {studyReferenceOrTestCode}
                    </button>
                }
                cellType="td"
                testLabel=""
                styles={{
                    main: {
                        minWidth: 300,
                        maxWidth: 300,
                        position: "sticky",
                        left: 0,
                        zIndex: 2,
                    },
                    wrapper: {},
                    value: {
                        alignItems: "center",
                    },
                }}
                mainProps={{
                    colSpan: 1,
                    align: "center",
                }}
            />
        );
    };

    const renderScoreStickyOtherCell = (col, i, datum) => {
        const mainStyles = col === "Consumer Filters" ? { minWidth: 200, maxWidth: 200, left: i * 200 + 100 } : { minWidth: 200, maxWidth: 200, position: "sticky", left: i * 200 + 100, zIndex: 2 };
        return (
            <TableCellWithOuterSeparator
                key={col + i}
                value={col === "Sample Size" ? <HeaderSampleSize productData={datum} /> : returnConsumerFilter(datum)}
                cellType="td"
                testLabel=""
                styles={{
                    main: mainStyles,
                    wrapper: {
                        justifyContent: "space-between",
                    },
                    value: {
                        alignItems: "center",
                        justifyContent: "center",
                    },
                }}
                mainProps={{
                    colSpan: 1,
                    align: "center",
                    justifyContent: "center",
                }}
            />
        );
    };

    const getScoreStickyCells = (stickyColumns, datum, productCodeTitle, studyReferenceOrTestCode) => {
        const stickyCells = stickyColumns.map((col, i) => {
            return i === 0 ? renderScoreStickyFirstCell(col, i, datum, productCodeTitle, studyReferenceOrTestCode) : renderScoreStickyOtherCell(col, i, datum);
        });

        return stickyCells;
    };

    const returnAnalysisScoresRow = () => {
        const _roww = draggableColumns.map((datum) => {
            const productCodeTitle = datum.title;
            const productCodeCoreDataId = datum.coreDataId;
            const className = getProductColumnClassName(productCodeTitle, productCodeCoreDataId, productColumnVisibility);
            const studyReferenceOrTestCode = selectedStudy?.find((study) => study.id === datum.coreDataId)?.study_reference__title;
            const _trs = (
                <tr className={className}>
                    {getScoreStickyCells(stickyColumns, datum, productCodeTitle, studyReferenceOrTestCode)}
                    {headerConfig.flatMap((macro, index) => {
                        return getScoreCells(macro, index, datum, productCodeTitle, productCodeCoreDataId);
                    })}
                </tr>
            );
            return _trs;
        });
        return _roww;
    };

    // column drag and drop logic

    const handleProductClick = (e, info) => {
        setProductDetailsDialogueOpen(true);
        setSelectedProductInfo(info);
    };

    return (
        <div>
            <VariableVisibility
                mappings={variableVisibilityMappings}
                moduleName={moduleName}
                pageName="fragrance"
                view={"table"}
            />
            <ColumnVisibility
                productsSecondaryData={draggableColumns}
                pageName="fragrance"
                btnName="Products Visibility"
                showSeperationLine={true}
            />
            <div
                className="table-responsive"
                style={{
                    width: "100%",
                    position: "relative",
                    minHeight: "584px",
                    top: -25,
                }}
            >
                <table
                    ref={tableRef}
                    className="table text-sm analysis-table"
                    style={{
                        background: "transparent",
                        borderCollapse: "separate",
                        borderSpacing: "0",
                    }}
                >
                    <thead>{returnTableHeader()}</thead>
                    <tbody ref={tbodyRef}>
                        {Object.keys(tTestResult).length > 0 && significanceToggle && returnSignificanceRow()}
                        {box && returnBoxAnalysisLabelRow()}
                        {returnAnalysisScoresRow()}
                    </tbody>
                </table>
            </div>
            {!["overall", "claims", "fragranceMetrics", "secondary"].includes(moduleName) && (
                <SkewTabs
                    tableData={{
                        productsSecondaryDataState: productsSecondaryData,
                    }}
                    settings={{
                        activeTab: activeTab,
                        setActiveTab: setActiveTab,
                    }}
                    analysisView={{
                        moduleName: moduleName,
                        mappings: mappings,
                        analysisTypeValue: analysisTypeValue,
                        boxAnalysisTypeSelection: boxAnalysisTypeSelection,
                    }}
                />
            )}
            <p
                style={{
                    color: "#495057",
                    fontWeight: 600,
                    fontSize: 12,
                    margin: 0,
                    textAlign: "right",
                }}
            >
                {"Likert scale size is indicated in brackets () next to each variable"}
            </p>
            <AnalysisTableBottomLabel
                actionStandard={{
                    actionStandardKpis: actionStandardKpis,
                    actionStandardData: actionStandardData,
                    showActionStandardsInfo: true,
                }}
                tTest={{
                    tTestLegends: tTestLegends,
                    isFisher: isFisher,
                    tTestResult: tTestResult,
                    testLabel: testLabel,
                }}
                analysisView={{
                    moduleName: bottomLabelModuleName,
                    isTableShown: isTableShown,
                }}
                cohensDLegendShown={cohensDResult.length > 0}
                pageContext={pageContext}
            />
            {productDetailsDialogueOpen && (
                <ProductInfo
                    settings={{
                        productDetailsDialogueOpen: productDetailsDialogueOpen,
                        setProductDetailsDialogueOpen: setProductDetailsDialogueOpen,
                    }}
                    info={selectedProductInfo}
                    productsSecondaryData={productsSecondaryData}
                />
            )}
        </div>
    );
}

AnalysisTableTransposed.propTypes = {
    productsSecondaryData: PropTypes.array,
    tTest: PropTypes.object,
    handleFilterSubmit: PropTypes.func,
    analysisLevelData: PropTypes.object,
    settings: PropTypes.object,
    isTableShown: PropTypes.bool,
    pageContext: PropTypes.string,
};
export default AnalysisTableTransposed;
